import {Box, Divider, Flex, P, Title} from 'exsportia-components'
import {TextType} from 'exsportia-components/dist/types'
import React, {FC, ReactNode, useMemo} from 'react'

import {safeRound} from '../../helpers/logic'
import {TranslatorScope} from '../../locale/types'
import {GradientLoader} from '../../ui/components/gradient-loader/gradient-loader'
import {useLocale} from '../../utils/i18n'
import {PriceRow} from '../price-row/price-row'

export type PricingElementType = {
  id?: string
  hint?: string
  price: number
  subLine?: boolean
  text: ReactNode | TextType
  totalPrice?: number
  count?: number
  subtext?: ReactNode | string
  checked?: boolean
  discount?: number
  withCheck?: boolean
  initialDiscount?: number
  isNegative?: boolean
  onCheckChange?: () => void
}

export type PricingPromo = {
  code: string
  amount: number
  // A pass code that takes the amount off every payment, not only this one (WBA#1284).
  everyPayment?: boolean
} | null

export type AdditionalFeeOrDiscount = {
  price: number
  isDiscount: boolean
  /** The server's words for the line, e.g. "Membership – Unlimited Monthly"; older servers send none. */
  label?: string
} | null

export type PricingDetailsProps = {
  title?: string | null
  discount: number
  isLoading: boolean
  currency: TextType
  total?: number | null
  isProcessing?: boolean
  withoutTotal?: boolean
  withoutTitle?: boolean
  secondaryItems?: boolean
  initialDiscount?: number
  predefinedLength?: number
  values: PricingElementType[]
  titleStyleType?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
  withDividerBetweenItems?: boolean
  additionalFeeOrDiscount?: AdditionalFeeOrDiscount
  withoutCurrency?: boolean
  // Promo code discount, shown as its own row after pass coverage and before Total.
  promo?: PricingPromo
  withYouSave?: boolean
  totalLabel?: string
}

export const PricingDetails: FC<PricingDetailsProps> = ({
  title,
  total,
  values,
  currency,
  discount,
  isLoading,
  isProcessing,
  titleStyleType,
  withoutTotal = false,
  withoutTitle = false,
  secondaryItems = false,
  initialDiscount = 0,
  predefinedLength = 1,
  withDividerBetweenItems = false,
  additionalFeeOrDiscount = null,
  withoutCurrency = false,
  promo = null,
  withYouSave = true,
  totalLabel,
}) => {
  const {l} = useLocale()

  const valuesMarkup = useMemo(
    () => {
      const currencyToUse = withoutCurrency ? ('' as TextType) : currency
      return (
        <>
        {isLoading ? (
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          [...Array(predefinedLength)].map((_, i) => (
            <Flex key={i} width='100%' mt={i === 0 ? '' : '16px'}>
              <GradientLoader height='32px' />
            </Flex>
          ))
        ) : (
          <Flex
            flexDirection='column'
            gap='12px'
          >
            {values.map((element, index) => (
              <React.Fragment key={index}>
                {withDividerBetweenItems && index !== 0 && element.subLine !== true && <Divider settings={{m: 0}} />}
                <PriceRow
                  id={element.id}
                  text={element.text}
                  price={element.price}
                  currency={currencyToUse}
                  subLine={element.subLine}
                  count={element.count}
                  isLoading={isProcessing}
                  checked={element.checked}
                  subtext={element.subtext}
                  isNegative={element.isNegative}
                  isDisabled={index === 0}
                  key={`price-row-${index}`}
                  withCheck={element.withCheck}
                  secondaryItems={secondaryItems}
                  onCheckChange={element.onCheckChange}
                />
              </React.Fragment>
            ))}
          </Flex>
        )}
        <Divider />
        {!isLoading && initialDiscount > 0 && (
          <Flex mt='12px'>
            <PriceRow
              bold
              isNegative
              currency={currencyToUse}
              price={initialDiscount}
              text={l('initialDiscount')}
            />
          </Flex>
        )}
        {!isLoading && discount !== 0 && (
          <Flex mt='12px'>
            <PriceRow
              bold
              isNegative
              price={discount}
              currency={currencyToUse}
              text={l('discount')}
            />
          </Flex>
        )}
        {!isLoading && promo && promo.amount > 0 && (
          <Flex mt='12px'>
            <PriceRow
              bold
              accent
              isNegative
              price={promo.amount}
              currency={currencyToUse}
              text={l('promo_line', {code: promo.code})}
            />
          </Flex>
        )}
        {!isLoading && additionalFeeOrDiscount && (
          <Flex mt='12px'>
            <PriceRow
              bold
              isNegative={additionalFeeOrDiscount.isDiscount}
              price={additionalFeeOrDiscount.price}
              currency={currencyToUse}
              text={additionalFeeOrDiscount.label || l(additionalFeeOrDiscount.isDiscount ? 'additionalDiscount' : 'additionalFee')}
            />
          </Flex>
        )}
        {!withoutTotal &&
          (isLoading ? (
            <Flex width='100%' mt='16px'>
              <GradientLoader />
            </Flex>
          ) : (
            <Flex mt='16px'>
              <PriceRow
                bold
                text={totalLabel || l('total')}
                currency={currencyToUse}
                price={total ?? (values.reduce((acc, {price}) => Number(price) + acc, 0) - discount - initialDiscount - (promo?.amount || 0))}
              />
            </Flex>
          ))
        }
        {!isLoading && !withoutTotal && withYouSave && promo && promo.amount > 0 && (
          <Flex mt='4px' justifyContent='flex-end'>
            <P color='primary' fontSize='label'>
              {l(promo.everyPayment ? 'promo_saves_every_payment' : 'promo_you_save', {
                amount: currencyToUse
                  ? l(`${String(currencyToUse)}_symbolic` as TranslatorScope, {value: safeRound(promo.amount)})
                  : safeRound(promo.amount),
              })}
            </P>
          </Flex>
        )}
      </>
    )
  }, [currency, l, values, isLoading, discount, initialDiscount, additionalFeeOrDiscount, withoutTotal, total, predefinedLength, isProcessing, withDividerBetweenItems, secondaryItems, withoutCurrency, promo, withYouSave, totalLabel])

  return (
    <Box>
      <Flex mb={'12px'}>
        {!withoutTitle && (
          <Title
            styleType={titleStyleType || 'h3'}
            text={title || l('labels_pricingAndPayments')}
          />
        )}
      </Flex>
      {valuesMarkup}
    </Box>
  )
}
